/**
 * Checks if a given text is a valid URL.
 * @param text - Text to check
 */
export const isValidUrl = (text: string) => {
  try {
    new URL(text);
    return true;
  } catch (err) {
    return false;
  }
};

/**
 * Extracts meaningful text from a URL.
 * Algorithm:
 * 1. Parse URL
 * 2. Remove 'www.' prefix if present
 * 3. Extract path without trailing slash
 * 4. If path has content besides '/', use the last segment
 * 5. If no useful path, just return the domain name
 * 6. If URL parsing fails, return a portion of the original URL
 * @param url - URL to extract text from
 */
export const extractMeaningfulTextFromUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);

    // Remove 'www.' prefix if present
    const hostname = urlObj.hostname.replace(/^www\./, '');

    // Extract path without trailing slash
    const path = urlObj.pathname.replace(/\/$/, '');

    // If path has content besides '/', use the last segment
    if (path && path !== '/') {
      const pathSegments = path.split('/').filter(Boolean);
      if (pathSegments.length > 0) {
        let lastSegment = decodeURIComponent(
          pathSegments[pathSegments.length - 1]
        );

        // Replace hyphens and underscores with spaces
        lastSegment = lastSegment.replace(/[-_]/g, ' ');

        // Remove file extensions if present
        lastSegment = lastSegment.replace(/\.\w+$/, '');

        // Capitalize first letter of each word
        lastSegment = lastSegment.replace(/\b\w/g, c => c.toUpperCase());

        return lastSegment;
      }
    }

    // If no useful path, just return the domain name
    return hostname;
  } catch (e) {
    // If URL parsing fails, return a portion of the original URL
    const match = url.match(/\/\/([^/]+)\/([^/]+)/);
    if (match) {
      return match[1] || url.substring(0, 30);
    }
    return url.substring(0, 30);
  }
};

/**
 * Extracts and validates clipboard text as a URL
 *
 * @param event - The clipboard event
 * @param validateUrl - Function to validate URLs
 * @returns The validated URL or null if invalid
 */
export const extractValidUrl = (
  event: ClipboardEvent,
  validateUrl: (url: string) => boolean
): string | null => {
  if (event.clipboardData === null) {
    return null;
  }

  const clipboardText = event.clipboardData.getData('text');
  return validateUrl(clipboardText) ? clipboardText : null;
};
